/**
 * Module-level store for KBUploadPage state.
 * Persists across React Router navigations (in-memory singleton).
 * File objects cannot be serialized, so this is intentionally in-memory only.
 *
 * Also manages the beforeunload listener so the browser warns on refresh
 * even when the user has navigated away from the upload page.
 */

interface FileQueueItem {
  id: string;
  file: File;
  status: 'pending' | 'validating' | 'valid' | 'invalid' | 'uploading' | 's3_uploaded' | 'queued' | 'error';
  progress: number;
  error?: string;
  validationErrors?: string[];
  fileUrl?: string;
  documentId?: string;
}

export interface KBUploadSnapshot {
  fileQueue: FileQueueItem[];
  currentStep: number;
  uploadSummary: { uploaded: number; queued: number; failed: number } | null;
  error: string | null;
}

let _snapshot: KBUploadSnapshot | null = null;

function hasPendingFiles(state: KBUploadSnapshot): boolean {
  return state.fileQueue.some(f => f.status !== 'error' && f.status !== 'queued');
}

function onBeforeUnload(e: BeforeUnloadEvent) {
  e.preventDefault();
}

function updateBeforeUnloadListener() {
  const shouldWarn = _snapshot !== null && hasPendingFiles(_snapshot);
  // Always remove first to avoid duplicate listeners
  window.removeEventListener('beforeunload', onBeforeUnload);
  if (shouldWarn) {
    window.addEventListener('beforeunload', onBeforeUnload);
  }
}

export const kbUploadStore = {
  save(state: KBUploadSnapshot) {
    _snapshot = state;
    updateBeforeUnloadListener();
  },
  restore(): KBUploadSnapshot | null {
    return _snapshot;
  },
  clear() {
    _snapshot = null;
    updateBeforeUnloadListener();
  },
};
